import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { type CustomTriggerProps } from '@atlaskit/dropdown-menu';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { type NewCoreIconProps } from '@atlaskit/icon/types';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageContext } from '@atlassian/post-office-context/message-context';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { type ComponentProps, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { AdControlsDropdown } from './components/dropdownMenu';
import { PrivacyPolicyLink, WhyAmISeeingThisModal } from './components/modal';
import { showStandaloneAdControlsFlag } from './utils/standaloneDismissalFlag';
import { type JSX } from 'react/jsx-runtime';

export { PrivacyPolicyLink };

type DropdownTriggerProps = CustomTriggerProps<HTMLButtonElement> & {
	name: string;
	iconSize?: NewCoreIconProps['size'];
	iconColor?: NewCoreIconProps['color'];
	spacing?: 'compact' | 'default';
	ariaLabelledby?: string;
};

const DropdownTrigger = ({
	triggerRef,
	name,
	iconSize = 'small',
	iconColor,
	spacing = 'default',
	ariaLabelledby,
	...triggerProps
}: DropdownTriggerProps) => {
	const intl = useIntl();
	const ariaLabel = intl.formatMessage(
		{
			id: 'post-office.ad-controls.dropdown-menu-trigger.aria-label',
			defaultMessage: '{messageInstanceId} options menu',
			description:
				'Aria label for the ad controls options dropdown menu trigger button, including the message instance ID',
		},
		{ messageInstanceId: name },
	);
	const tooltip = intl.formatMessage({
		id: 'post-office.ad-controls.dropdown-menu-trigger.tooltip',
		defaultMessage: 'More actions',
		description: 'Tooltip for ad-controls dropdown',
	});

	return (
		<Tooltip content={tooltip} isScreenReaderAnnouncementDisabled={true}>
			<span hidden={true}>{ariaLabel}</span>
			<IconButton
				{...triggerProps}
				label={tooltip}
				id="ad-controls-trigger"
				appearance="subtle"
				ref={triggerRef}
				spacing={spacing}
				icon={(iconProps) => (
					<ShowMoreHorizontalIcon {...iconProps} color={iconColor} size={iconSize} />
				)}
				aria-labelledby={
					ariaLabelledby ? `${ariaLabelledby} ad-controls-trigger` : 'ad-controls-trigger'
				}
			/>
		</Tooltip>
	);
};

/*--ad controls--*/

type BaseAdControlsProps = {
	onDismissed?: (messageInstanceId: string) => void;
	onRemindMeLater?: (messageInstanceId: string) => void;
	iconSize?: NewCoreIconProps['size'];
	iconColor?: NewCoreIconProps['color'];
	zIndex?: number;
	additionalAnalyticsAttributes?: Record<string, string>;
	flagAdControls?: boolean;
	showRemindMeLater?: boolean | undefined;
	spacing?: 'compact' | 'default';
	customDropdownItems?: React.ReactNode[];
	ariaLabelledby?: string;
};

// Define a type for ad-controls props for when whyAmISeeingThis modal is used
type ShowWhyAmISeeingThisProps = {
	showWhyAmISeeingThis: boolean;
	/**
	 * This is a prop that is used to pass the content to the WhyAmISeeingThisModal.
	 * The content is used to provide the user with a reason why they are seeing the message.
	 * Reasons are required to be provided.
	 */
	whyAmISeeingThisModalContent: ComponentProps<typeof WhyAmISeeingThisModal>['content'];
};

// Define a type for ad-controls props for when whyAmISeeingThis modal is not used
type HideWhyAmISeeingThisProps = {
	showWhyAmISeeingThis: false;
	whyAmISeeingThisModalContent?: never;
};

export type Props = BaseAdControlsProps & (ShowWhyAmISeeingThisProps | HideWhyAmISeeingThisProps);

/**
 * AdControls component is used to render the dropdown menu for the ad controls.
 * This should be displayed next to each message and allows users to dismiss the message or see why they are seeing it.
 */
export const AdControls = ({
	onDismissed,
	onRemindMeLater,
	iconSize,
	iconColor,
	whyAmISeeingThisModalContent,
	showWhyAmISeeingThis,
	showRemindMeLater,
	additionalAnalyticsAttributes = {},
	zIndex,
	spacing = 'default',
	customDropdownItems,
	ariaLabelledby,
}: Props): JSX.Element => {
	const [isWhyAmISeeingThisModelOpened, setIsWhyAmISeeingThisModelOpened] = useState(false);
	const { messageDismissed, messageAcknowledged } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { messageInstanceId } = useMessageContext();
	const [currIconColor, setCurrIconColor] = useState<NewCoreIconProps['color']>(iconColor);
	if (!messageInstanceId) {
		throw new Error('messageInstanceId is required');
	}

	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const intl = useIntl();
	const { messageSnoozed } = useMessageEvent();
	const handleRemindMeLater = async () => {
		// https://developer.atlassian.com/platform/post-office/working-with-post-office/technical-deep-dives/pre-defined-filters/#snooze-check-filter
		void messageSnoozed(1); // DEPRECATED, but used alongside the messageInstanceClicked function just in case. See above page for documentation
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'remindMeLater',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'snooze',
				snoozeDuration: 1, // Snooze message for 1 day
			},
			additionalMetadataFromFrontend: {},
		});
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'remindMeLater',
			},
			{ ...additionalAnalyticsAttributes },
		);
		if (messageInstanceId && typeof onRemindMeLater === 'function') {
			// integrating with useMessagePlacementHooks removes the need to pass this callback
			onRemindMeLater(messageInstanceId);
		}
		// rendered outside this component's tree, since onRemindMeLater above unmounts it immediately
		showStandaloneAdControlsFlag(messageInstanceId, intl);
	};

	const handleNotInterested = async () => {
		await messageDismissed();
		await messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'notInterested',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {},
		});
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'notInterested',
			},
			{ ...additionalAnalyticsAttributes },
		);

		if (messageInstanceId && typeof onDismissed === 'function') {
			// integrating with useMessagePlacementHooks removes the need to pass this callback
			onDismissed(messageInstanceId);
		}
		// rendered outside this component's tree, since onDismissed above unmounts it immediately
		showStandaloneAdControlsFlag(messageInstanceId, intl);
	};

	const handleWhyAmISeeingThis = async () => {
		setIsWhyAmISeeingThisModelOpened(true);
		await messageAcknowledged();
		await messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'adControlsReasons',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'acknowledge',
			},
			additionalMetadataFromFrontend: {},
		});
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'adControlsReasons',
			},
			{ ...additionalAnalyticsAttributes },
		);
	};

	const fireDropdownMenuOpenAnalyticsEvent = useCallback(() => {
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'adControlsDropdown',
			},
			{ ...additionalAnalyticsAttributes },
		);
	}, [additionalAnalyticsAttributes, fireAnalyticsEvent]);

	return (
		<>
			<DropdownMenu
				testId="post-office-ad-controls-dropdown"
				trigger={({ triggerRef, ...triggerProps }) => (
					<DropdownTrigger
						name={messageInstanceId}
						triggerRef={triggerRef}
						iconSize={iconSize}
						iconColor={currIconColor}
						spacing={spacing}
						ariaLabelledby={ariaLabelledby}
						{...triggerProps}
					/>
				)}
				{...(zIndex && { zIndex })}
				onOpenChange={(args) => {
					if (args.isOpen) {
						fireDropdownMenuOpenAnalyticsEvent();
						setCurrIconColor(token('color.icon.selected'));
					} else {
						setCurrIconColor(iconColor);
					}
				}}
			>
				<AdControlsDropdown
					onNotInterested={handleNotInterested}
					onWhyAmISeeingThis={handleWhyAmISeeingThis}
					onRemindMeLater={handleRemindMeLater}
					showWhyAmISeeingThis={showWhyAmISeeingThis}
					showRemindMeLater={showRemindMeLater ? true : false}
					customDropdownItems={customDropdownItems}
				/>
			</DropdownMenu>
			{showWhyAmISeeingThis && (
				<WhyAmISeeingThisModal
					content={whyAmISeeingThisModalContent}
					isOpen={isWhyAmISeeingThisModelOpened}
					additionalAnalyticsAttributes={additionalAnalyticsAttributes}
					onClose={() => {
						setIsWhyAmISeeingThisModelOpened(false);
						fireAnalyticsEvent(
							{
								eventType: 'ui',
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'adControlsReasonsClose',
							},
							{ ...additionalAnalyticsAttributes },
						);
					}}
				/>
			)}
		</>
	);
};
